import { useParams, Link } from 'react-router-dom';
import { articleDetails, type ArticleDetail } from '@/mocks/articles';
import { allCategoryArticles } from '@/mocks/category';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article: ArticleDetail | undefined = id ? articleDetails[id] : undefined;

  if (!article) {
    return (
      <main className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-5 flex items-center justify-center rounded-2xl bg-background-100">
            <i className="ri-file-unknow-line text-foreground-400 text-3xl"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">文章未找到</h1>
          <p className="text-sm text-foreground-600 mb-6">该文章可能已被删除或链接有误</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i> 返回首页
          </Link>
        </div>
      </main>
    );
  }

  const relatedArticles = article.relatedIds
    .map((rid) => {
      for (const cat of Object.values(allCategoryArticles)) {
        const found = cat.find((a) => a.id === rid);
        if (found) return found;
      }
      return null;
    })
    .filter(Boolean);

  return (
    <>
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.subtitle,
          datePublished: article.date,
          author: { '@type': 'Person', name: article.author },
          image: article.coverImage,
          publisher: {
            '@type': 'Organization',
            name: '日韩在线',
            logo: { '@type': 'ImageObject', url: 'https://rikkan-online.com/logo.png' },
          },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://rikkan-online.com' },
            { '@type': 'ListItem', position: 2, name: article.category, item: `https://rikkan-online.com/category?tab=${article.categorySlug}` },
            { '@type': 'ListItem', position: 3, name: article.title },
          ],
        }}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${article.title} - 日韩在线`,
          description: article.subtitle,
          url: `https://rikkan-online.com/article/${article.id}`,
        }}
      />

      <main className="min-h-screen pt-16">
        <article>
          {/* Cover */}
          <div className="relative w-full h-[300px] md:h-[420px] overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-4 md:p-8">
              <div className="max-w-4xl mx-auto">
                <span className="inline-block px-3 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-full mb-3">
                  {article.category}
                </span>
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-3 leading-tight">
                  {article.title}
                </h1>
                <p className="text-background-50/80 text-sm md:text-base max-w-2xl">
                  {article.subtitle}
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: article.category, path: `/category?tab=${article.categorySlug}` },
                { label: article.title },
              ]}
            />

            <div className="flex items-center gap-4 py-4 border-b border-background-200/70 mb-8">
              <img
                src={article.authorAvatar}
                alt={article.author}
                className="w-10 h-10 rounded-full object-cover"
              />
              <div>
                <p className="text-sm font-medium text-foreground-900">{article.author}</p>
                <p className="text-xs text-foreground-500">
                  {article.date} · {article.readTime}
                </p>
              </div>
            </div>

            <div className="prose-custom max-w-none pb-10">
              {article.content.map((section, idx) => {
                switch (section.type) {
                  case 'paragraph':
                    return (
                      <p key={idx} className="text-foreground-800 leading-relaxed mb-5 text-[15px]">
                        {section.content}
                      </p>
                    );
                  case 'heading':
                    return (
                      <h2 key={idx} className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-10 mb-4">
                        {section.content}
                      </h2>
                    );
                  case 'image':
                    return (
                      <figure key={idx} className="my-8">
                        <img
                          src={section.content}
                          alt={section.caption || ''}
                          className="w-full rounded-xl"
                          title={section.caption ? `${section.caption} - 日韩在线` : undefined}
                        />
                        {section.caption && (
                          <figcaption className="text-center text-sm text-foreground-500 mt-2.5">
                            {section.caption}
                          </figcaption>
                        )}
                      </figure>
                    );
                  case 'quote':
                    return (
                      <blockquote
                        key={idx}
                        className="relative pl-5 my-8 border-l-[3px] border-primary-400 bg-primary-50/50 py-4 pr-4 rounded-r-lg"
                      >
                        <p className="text-foreground-700 italic text-[15px] leading-relaxed">
                          {section.content}
                        </p>
                      </blockquote>
                    );
                  default:
                    return null;
                }
              })}

              {/* Tags */}
              <div className="flex items-center flex-wrap gap-2 mt-10 pt-8 border-t border-background-200/70">
                <span className="text-sm text-foreground-600 mr-1">
                  <i className="ri-price-tag-3-line"></i>
                </span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700 font-medium"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-20 bg-background-100" aria-label="相关文章">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group bg-background-50 rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:bg-background-200/50"
                  >
                    <div className="relative overflow-hidden aspect-[16/9]">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        title={`${ra.title} - 日韩在线`}
                      />
                      <span className="absolute top-3 left-3 px-2.5 py-1 bg-primary-500/90 text-background-50 text-xs font-medium rounded-full">
                        {ra.category}
                      </span>
                    </div>
                    <div className="p-4 md:p-5">
                      <h3 className="text-base font-heading font-bold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h3>
                      <p className="text-sm text-foreground-600 line-clamp-2 mb-3">
                        {ra.excerpt}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-foreground-500">
                        <span>{ra.date}</span>
                        <span>·</span>
                        <span>{ra.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}